import React, { useState } from 'react';
import { useProgram } from '../contexts/ProgramContext';
import ProgramCard from '../components/programs/ProgramCard';
import ResponsiveGrid from '../components/layout/ResponsiveGrid';
import {
  BookOpen,
  Search,
  Clock,
  Users,
  Award,
  TrendingUp
} from 'lucide-react';

const Programs = () => {
  const { programs, loading } = useProgram();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  // Obtenir les catégories uniques
  const categories = [...new Set(programs.map(program => program.category))];

  // Filtrer les programmes
  const filteredPrograms = programs.filter(program => {
    const matchesSearch = program.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         program.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         program.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || program.category === categoryFilter;
    const matchesLevel = levelFilter === 'all' || program.level === levelFilter;
    
    return matchesSearch && matchesCategory && matchesLevel;
  });

  // Trier les programmes
  const sortedPrograms = [...filteredPrograms].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.students - a.students;
      case 'rating':
        return b.rating - a.rating;
      case 'price_low':
        return a.price - b.price;
      case 'price_high':
        return b.price - a.price;
      case 'duration':
        return parseInt(a.duration) - parseInt(b.duration);
      default:
        return 0;
    }
  });

  // Statistiques
  const totalPrograms = programs.length;
  const totalStudents = programs.reduce((sum, program) => sum + program.students, 0);
  const averageRating = programs.length > 0 
    ? (programs.reduce((sum, program) => sum + program.rating, 0) / programs.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-300 dark:bg-gray-600 rounded-lg h-96"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Programmes d'Apprentissage
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Suivez des parcours structurés pour maîtriser des compétences complètes et obtenir des certificats reconnus.
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 text-center">
          <BookOpen className="w-8 h-8 text-blue-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {totalPrograms}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Programmes
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 text-center">
          <Users className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {totalStudents.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Étudiants
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 text-center">
          <Award className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {averageRating}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Note moyenne
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 text-center">
          <TrendingUp className="w-8 h-8 text-purple-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {programs.length > 0
              ? `${Math.round((programs.reduce((s, p) => s + (p.rating || 0), 0) / programs.length / 5) * 100)}%`
              : '—'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Satisfaction
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un programme..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Toutes les catégories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tous les niveaux</option>
              <option value="Débutant">Débutant</option>
              <option value="Intermédiaire">Intermédiaire</option>
              <option value="Avancé">Avancé</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="popular">Plus populaires</option>
              <option value="rating">Meilleures notes</option>
              <option value="price_low">Prix croissant</option>
              <option value="price_high">Prix décroissant</option>
              <option value="duration">Durée croissante</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des programmes */}
      {sortedPrograms.length > 0 ? (
        <ResponsiveGrid
          cols={{ default: 1, md: 2, lg: 3 }}
          gap={6}
        >
          {sortedPrograms.map((program) => (
            <ProgramCard 
              key={program.id} 
              program={program} 
              showProgress={true}
            />
          ))}
        </ResponsiveGrid>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <BookOpen className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Aucun programme trouvé
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Essayez de modifier vos filtres de recherche.
          </p>
        </div>
      )}

      {/* Section d'information */}
      <div className="bg-gradient-to-r from-primary to-secondary rounded-lg p-8 text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">
            Pourquoi choisir nos programmes ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="text-center">
              <Clock className="w-8 h-8 mx-auto mb-2" />
              <h3 className="font-semibold mb-2">Parcours structurés</h3>
              <p className="text-sm opacity-90">
                Suivez un chemin d'apprentissage logique et progressif
              </p>
            </div>
            <div className="text-center">
              <Award className="w-8 h-8 mx-auto mb-2" />
              <h3 className="font-semibold mb-2">Certificats reconnus</h3>
              <p className="text-sm opacity-90">
                Obtenez des certificats valorisés par les employeurs
              </p>
            </div>
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto mb-2" />
              <h3 className="font-semibold mb-2">Communauté active</h3>
              <p className="text-sm opacity-90">
                Rejoignez des milliers d'étudiants motivés
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Programs;
